import React, { ReactNode, useEffect, useState } from "react"
import Lightbox from "yet-another-react-lightbox"
import Zoom from "yet-another-react-lightbox/plugins/zoom"
import Video from "yet-another-react-lightbox/plugins/video"
import "yet-another-react-lightbox/styles.css"

import { ImgNoImage } from "@/assets/images"
import { AttachmentType } from "@/constants/attachment-type"
import { HugeiconsIcon, Icons } from "@/components/icons"
import { cn } from "@/lib/utils"

const VIDEO_EXTENSIONS = [".mp4"]

function isVideoUrl(url: string): boolean {
  if (!url || typeof url !== "string") return false
  try {
    const pathname = new URL(url, "https://dummy").pathname.toLowerCase()
    return VIDEO_EXTENSIONS.some((ext) => pathname.endsWith(ext))
  } catch {
    return false
  }
}

interface ImageWithFallbackProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  fallback?: ReactNode
  fallbackWrapperClassName?: string
  fallbackClassName?: string
  baseClassName?: string
  fill?: boolean
  sizeFit?: boolean
  /** "image" | "video" | "auto". When "auto", detect from URL (e.g. .mp4, .webm). Default "auto". */
  mediaType?: AttachmentType
  /** ClassName applied when rendering a video (e.g. max-h, object-contain). When not set, uses className. */
  videoClassName?: string
  videoAutoPlay?: boolean
  videoControls?: boolean
  videoPreload?: "none" | "metadata" | "auto"
  videoClickToPlay?: boolean
  /** When true, show maximize button to preview image / video in high-res lightbox modal */
  previewable?: boolean
  /** High resolution url for preview slide */
  previewSrc?: string
}

export const ImageWithFallback = ({
  fallback,
  fallbackClassName,
  fallbackWrapperClassName,
  baseClassName,
  fill,
  className,
  videoClassName,
  videoAutoPlay = true,
  videoControls = true,
  videoPreload = "metadata",
  videoClickToPlay = false,
  style,
  sizeFit,
  mediaType = AttachmentType.AUTO,
  loading = "eager",
  decoding = "async",
  previewable = false,
  previewSrc,
  ...props
}: ImageWithFallbackProps) => {
  const [hasError, setHasError] = useState(false)
  const [isOpen, setIsOpen] = useState(false)

  // Handle fill prop by using absolute positioning
  const imageStyle: React.CSSProperties = fill
    ? {
        ...style,
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
      }
    : style || {}
  const imageClassName = fill ? cn("object-cover", className) : className

  // Convert StaticImageData/Blob to string if needed
  const getImageSrc = (
    src: string | Blob | { src?: string } | undefined
  ): string => {
    if (!src) return ""
    if (typeof src === "string") return src
    if (src instanceof Blob) {
      return URL.createObjectURL(src)
    }
    if (typeof src === "object" && "src" in src) return src.src || ""
    return String(src)
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resolvedSrc = getImageSrc(props.src as any)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fallbackSrc = getImageSrc(ImgNoImage as any)

  useEffect(() => {
    setHasError(false)
  }, [resolvedSrc])

  const isVideo =
    mediaType === AttachmentType.VIDEO ||
    (mediaType === AttachmentType.AUTO && isVideoUrl(resolvedSrc))

  const slides = isVideo
    ? [
        {
          type: "video" as const,
          sources: [
            {
              src: resolvedSrc,
              type: resolvedSrc.toLowerCase().endsWith(".mov") ? "video/quicktime" : "video/mp4",
            },
          ],
          autoPlay: true,
          controls: true,
        },
      ]
    : [
        {
          type: "image" as const,
          src: previewSrc || resolvedSrc,
        },
      ]

  const imgElement = (
    <img
      {...props}
      src={resolvedSrc}
      loading={loading}
      decoding={decoding}
      className={imageClassName}
      style={imageStyle}
      onError={(e) => {
        setHasError(true)
        props?.onError?.(e)
      }}
    />
  )

  return (
    <div
      className={cn(
        "bg-background group relative flex size-full items-center justify-center overflow-hidden",
        sizeFit && "size-fit",
        baseClassName
      )}
    >
      {previewable && !hasError && resolvedSrc && isVideo && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation()
            setIsOpen(true)
          }}
          className="absolute top-2 right-2 z-20 flex size-8 cursor-pointer items-center justify-center rounded-full bg-black/55 text-white opacity-0 transition-opacity duration-200 hover:scale-105 hover:bg-black/85 group-hover:opacity-100 shadow-md backdrop-blur-sm border border-white/10"
          aria-label="Enlarge media"
        >
          <HugeiconsIcon icon={Icons.ArrowExpandDiagonal01} size={14} />
        </button>
      )}

      {hasError || !resolvedSrc ? (
        <div className={fallbackWrapperClassName}>
          {fallback ? (
            fallback
          ) : (
            <img
              src={fallbackSrc}
              alt="No Image"
              loading="eager"
              decoding="async"
              className={cn("object-cover", imageClassName, fallbackClassName)}
              style={imageStyle}
              {...(fill ? {} : { width: props.width, height: props.height })}
            />
          )}
        </div>
      ) : isVideo ? (
        <>
          <video
            src={resolvedSrc}
            controls={videoControls}
            autoPlay={videoAutoPlay}
            muted
            playsInline
            preload={videoPreload}
            className={cn(
              videoClassName ?? imageClassName,
              videoClickToPlay && "cursor-pointer"
            )}
            style={imageStyle}
            onClick={(event) => {
              if (!videoClickToPlay) return
              if (event.currentTarget.paused) {
                void event.currentTarget.play()
              } else {
                event.currentTarget.pause()
              }
            }}
            onError={() => setHasError(true)}
          />
          {videoClickToPlay && (
            <div className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/0 opacity-0 transition-all duration-200 group-hover:bg-black/20 group-hover:opacity-100">
              <div className="flex size-10 items-center justify-center rounded-full bg-black/55 text-white shadow-lg backdrop-blur-sm">
                <HugeiconsIcon icon={Icons.Play} size={20} />
              </div>
            </div>
          )}
        </>
      ) : (
        <div
          onClick={() => {
            if (previewable) {
              setIsOpen(true)
            }
          }}
          className={cn(
            "relative size-full flex items-center justify-center rounded-[inherit]",
            previewable && "cursor-pointer"
          )}
        >
          {imgElement}

          {previewable && (
            <div className="absolute inset-0 z-10 flex items-center justify-center rounded-[inherit] bg-black/30 opacity-0 transition-opacity duration-200 group-hover:opacity-100">
              <div className="flex items-center justify-center rounded-full bg-black/50 p-2 text-white shadow-md border border-white/10 hover:scale-105 transition-transform duration-200">
                <HugeiconsIcon icon={Icons.ArrowExpandDiagonal01} size={16} />
              </div>
            </div>
          )}
        </div>
      )}

      {previewable && isOpen && (
        <Lightbox
          open={isOpen}
          close={() => setIsOpen(false)}
          slides={slides}
          plugins={[Zoom, Video]}
          render={{
            buttonPrev: () => null,
            buttonNext: () => null,
          }}
          controller={{
            disableSwipeNavigation: true,
          }}
          zoom={{
            maxZoomPixelRatio: 5,
            scrollToZoom: true,
          }}
        />
      )}
    </div>
  )
}
